import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true, timeout: 45000 });

// AUTH FALLBACK: attach the localStorage session token as a Bearer header on every request. The httpOnly
// session cookie remains the PRIMARY auth transport; this Bearer fallback keeps login working when a browser
// drops the cross-site cookie (Cloudflare cookie partitioning / Safari ITP / incognito / custom domain on a
// different origin than the API). The backend accepts `Authorization: Bearer <session_token>` everywhere.
api.interceptors.request.use((config) => {
  try {
    const t = window.localStorage.getItem("wds_token");
    if (t) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
  } catch { /* localStorage unavailable — cookie path still applies */ }
  return config;
});

// P0 (2026-07-12/13): surface backend outages instead of white-screening. A network error / timeout / any
// gateway-style failure marks the backend DOWN (broadcast for the retry banner); any success marks it UP.
// The 2026-07-13 outage slipped past the old check because the k8s ingress returns a bare **404** (and can
// return 5xx) when the pod is DOWN — not a FastAPI JSON error. We now treat 404/500 as "pod down" UNLESS the
// response is a genuine application error (JSON body carrying `detail`), so real app 404s (deleted resource)
// don't false-fire the banner while an ingress/pod-down 404 does. Errors still propagate to callers.
const _announce = (up) => window.dispatchEvent(new CustomEvent("backend-status", { detail: { up } }));

const _isAppError = (err) => {
  const r = err && err.response;
  if (!r) return false;
  const ct = (r.headers && (r.headers["content-type"] || r.headers["Content-Type"])) || "";
  const hasDetail = r.data && typeof r.data === "object" && "detail" in r.data;
  return String(ct).includes("application/json") || hasDetail; // a structured FastAPI error = app is alive
};

api.interceptors.response.use(
  (res) => { _announce(true); return res; },
  (err) => {
    const status = err && err.response && err.response.status;
    const gatewayDown = !err.response || [502, 503, 504].includes(status) || err.code === "ECONNABORTED";
    // 404/500 that is NOT a structured app error => ingress/pod-down masquerading as one of these.
    const podDownMasquerade = [404, 500].includes(status) && !_isAppError(err);
    if (gatewayDown || podDownMasquerade) _announce(false);
    else _announce(true);
    return Promise.reject(err);
  }
);

export default api;
